import { AppErrorCode } from '../../../_models/app-error-code.enum';
import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Subject, Observable, defer, from, of } from 'rxjs';
import { catchError, concatMap, finalize, map, switchMap, takeUntil, toArray } from 'rxjs/operators';
import { NotificationService } from '../../../shared/services/notification.service';
import { SdsApiService, SdsUpdateDocumentRequest } from '../services/sds-api.service';
import { SdsArchiveFile } from './sds-archive-picker.component';
import { SdsPdfScannerService } from '../services/sds-pdf-scanner.service';
import { joinCasNumbers, mergeCasNumbersWithinLimit } from '../validators/cas-number.validator';

@Component({
  selector: 'app-sds-bulk-import',
  templateUrl: './sds-bulk-import.component.html',
  standalone: false
})
export class SdsBulkImportComponent implements OnDestroy {
  @Input() businessId = 0;
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() imported = new EventEmitter<void>();
  files: File[] = [];
  archiveFiles: SdsArchiveFile[] = [];
  archiveVisible = false;
  importing = false;
  errorKey = '';
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly api: SdsApiService, private readonly notifications: NotificationService,
    private readonly scanner: SdsPdfScannerService) {}

  get count(): number { return this.files.length + this.archiveFiles.length; }

  selectLocal(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selected = Array.from(input.files ?? []);
    input.value = '';
    this.errorKey = '';
    if (selected.some(file => !file.name.toLowerCase().endsWith('.pdf') || file.size < 5 || file.size > 20 * 1024 * 1024)) {
      this.errorKey = 'SDS_BULK_FILE_LIMIT';
      return;
    }
    const additions = selected.filter(file => !this.files.some(existing => existing.name === file.name && existing.size === file.size && existing.lastModified === file.lastModified));
    if (this.count + additions.length > 50 || [...this.files, ...additions].reduce((sum, file) => sum + file.size, 0) > 100 * 1024 * 1024) {
      this.errorKey = 'SDS_BULK_BATCH_LIMIT';
      return;
    }
    this.files = [...this.files, ...additions];
  }

  selectArchive(files: SdsArchiveFile[]): void {
    const additions = files.filter(file => !this.archiveFiles.some(existing => existing.id === file.id));
    this.errorKey = '';
    if (this.count + additions.length > 50) {
      this.errorKey = 'SDS_BULK_BATCH_LIMIT';
      return;
    }
    this.archiveFiles = [...this.archiveFiles, ...additions];
  }

  close(): void {
    if (this.importing) return;
    this.visibleChange.emit(false);
    this.files = [];
    this.archiveFiles = [];
    this.errorKey = '';
  }

  importFiles(): void {
    if (this.importing || this.count === 0) return;
    this.importing = true;
    this.errorKey = '';
    const files = [...this.files];
    const archiveFiles = [...this.archiveFiles];
    // Scan one PDF at a time to bound memory, preserving the multipart source order.
    const scans = [
      ...files.map(file => this.scanFile(file.name, of(file))),
      ...archiveFiles.map(file => this.scanFile(file.name,
        this.api.getArchivePdfPreviewFile(this.businessId, file.id, file.name, true)))
    ];
    from(scans).pipe(
      concatMap(scan => scan),
      toArray(),
      switchMap(documents => this.api.bulkImport(this.businessId, files, archiveFiles.map(file => file.id), documents)),
      takeUntil(this.destroy$),
      finalize(() => this.importing = false)
    )
      .subscribe({
        next: () => {
          this.importing = false;
          this.notifications.success('SDS_BULK_SUCCESS');
          this.imported.emit();
          this.close();
        },
        error: (error: { error?: { Code?: number } }) => {
          this.errorKey = error.error?.Code === AppErrorCode.Conflict ? 'SDS_BULK_CONFLICT' : 'SDS_BULK_ERROR';
        }
      });
  }

  private scanFile(name: string, source: Observable<File>): Observable<SdsUpdateDocumentRequest> {
    const product = (this.scanner.getProductName(name) || 'SDS').slice(0, 255);
    return source.pipe(
      switchMap(file => defer(() => this.scanner.scanDocument(file))),
      map(scan => ({
        Product: product,
        CASNumber: joinCasNumbers(mergeCasNumbersWithinLimit([], scan.cas.candidates).merged)
      })),
      // A preview or text-extraction failure must not prevent deferred data entry.
      catchError(() => of({ Product: product, CASNumber: '' }))
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
